/** @format */

import React, {FC, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,} from "react";
import {
    Composer,
    GiftedChat,
    GiftedChatProps,
    IMessage,
    InputToolbar,
    InputToolbarProps,
    Message,
    QuickRepliesProps,
    Reply,
    User,
} from "react-native-gifted-chat";
import {IRasaMedium, IRasaMessage, IRasaResponse} from "./types";
import HeaderWrap from "../../components/Header";
import ChatIcon from "../../Icons/ChatIcon";
import {
    ActivityIndicator,
    AppState,
    AppStateStatus,
    Image,
    Keyboard,
    KeyboardAvoidingView,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from "react-native";
import {createBotEmptyMessage, createNewBotMessage, createQuickUserReply, isValidNotEmptyArray,} from "./utils";
import {AppTheme, useTheme} from "../../theme/colors";
import {RasaUtils} from "../../utils/Rasa.utils";
import RenderRasaButtonIcons from "../../components/RenderRasaButtonIcons";
import {Storage} from "../../utils/Storage";
import {useFocusEffect} from "@react-navigation/native";
import {updatePassportAndWellbeingFormData,} from "../../utils/passport.utils";
import {ONETOONE_HOST, RASA_HOST, WEB_MAXWIDTH} from "../../../constants";
import NetInfo from "@react-native-community/netinfo";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {getSession, refreshIdTokenIfNeeded} from "../../utils/cognitoAuth"; // ✅ Cognito Utility Integration
import {Analytics} from "aws-amplify";
import {WebView} from 'react-native-webview';
import RNRestart from 'react-native-restart';
import {grey50} from "react-native-paper/lib/typescript/styles/themes/v2/colors";
import {Frame, IsMobile} from "../../theme";


//TODO: reset bot on destroy
export interface IRasaChat
  extends Omit<
    GiftedChatProps,
    "user" | "onSend" | "messages" | "onQuickReply"
  > {
    host: string;
    onSendMessFailed?: (error: unknown) => void;
    onEmptyResponse?: () => void;
    emptyResponseMessage?: string;
    userId?: string | number;
    userAvatar?: string;
    userName?: string;
    botName?: string;
    botAvatar?: string;
}

export interface IRasaChatHandles {
    resetMessages(): void;

    resetBot(): void;

    sendCustomMessage(message: string): void;
}

const lastChatVisit = "lastChatVisit";


const Chat = React.forwardRef<IRasaChatHandles, IRasaChat>((props, ref) => {
      const sentFirstMessage = useRef(false);
      const pingInterval = useRef<NodeJS.Timeout | null>(null);
      const [onetoone, setOnetoone] = useState(false);
      const ws = useRef<WebSocket | null>(null);
      const [eventsLoaded, setEventsLoaded] = useState(false);
      const theme = useTheme();
      const styles = getStyles(theme);
      const [_userName, set_userName] = useState("");
      const [_userId, set_UserId] = useState("");
      const [_email, set_Email] = useState("");
      const [_gender, set_Gender] = useState("");
      const [_university, set_University] = useState("");
      const [isTyping, setIsTyping] = useState(false);
      const [isConnected, setIsConnected] = useState(true);
      const [errorMessage, setErrorMessage] = useState(null);
      const [messages, setMessages] = useState<IMessage[]>([]);
      const [lastRasaCustomResponse, setLastRasaCustomResponse] =
        useState<IRasaResponse>();
      const [lastRasaMediumResponse, setLastRasaMediumResponse] =
        useState<IRasaMedium>();
      const [webviewURL, setWebviewURL] = useState<string | null>(null);

      const openExternalURL = (url: string) => {
          setWebviewURL(url);
      };

      const rootUrlAvatar = `https://nova.umo.services/dokumento/avatars`;


      const WebViewMessage = ({uri}: { uri: string }) => {
          const [token, setToken] = useState<string | null>(null);

          useEffect(() => {
              const fetchToken = async () => {
                  const idToken = await refreshIdTokenIfNeeded();
                  setToken(idToken);
              };
              fetchToken();
          }, []);

          if (!token) {
              return (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="small" color="#000"/>
                </View>
              );
          }

          return (
            <View style={{flex: 1}}>
                <TouchableOpacity
                  onPress={() => setWebviewURL(null)}
                  style={{
                      position: "absolute",
                      top: 40,
                      left: 20,
                      zIndex: 10,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      padding: 10,
                      borderRadius: 5,
                  }}
                >
                    <Text style={{color: "white"}}>Close</Text>
                </TouchableOpacity>
                <WebView
                  source={{
                      uri: webviewURL,
                      headers: {
                          Authorization: `Bearer ${token}`, // Use your token logic if needed
                      },
                  }}
                  style={{flex: 1}}
                />
            </View>
          );
      };


      const CustomRenderMessage = (props: any) => {
          const {currentMessage} = props;

          if (currentMessage?.text?.startsWith("https://")) {
              return (
                <TouchableOpacity
                  onPress={() => openExternalURL(currentMessage?.text)}
                >
                    <Text style={styles.button}>Review timesheet</Text>
                </TouchableOpacity>
              );
          } else if (
            currentMessage?.custom &&
            lastRasaWebviewResponse?.text?.startsWith("http")
          ) {
              return <WebViewMessage uri={lastRasaWebviewResponse.text as string}/>;
          }

          return <Message {...props} />;
      };


      const getNameAndId = async () => {
          let data = await Storage.getStorage();
          const localUserName = data.given_name || "";
          const localUserId = data.sub || "XXX";
          const localEmail = data.email || "";
          const localGender = data.gender || "unknown";
          const localUniversity = data.profile_university || "none";

          set_userName(localUserName);
          set_UserId(localUserId);
          set_Email(localEmail);
          set_Gender(localGender);
          set_University(localUniversity);

          return {
              userId: localUserId,
              userName: localUserName,
              email: localEmail,
              gender: localGender,
              university: localUniversity
          };
      };

      const {
          host = RASA_HOST,
          onSendMessFailed,
          onEmptyResponse,
          emptyResponseMessage,
          userId = _userId,
          userName = _userName,
          userAvatar = rootUrlAvatar + "/user.png",
          botName = "OneSpace",
          botAvatar = rootUrlAvatar + "/bot.png",
          ...giftedChatProp
      } = props;

      const rasaUtils = useMemo(() => new RasaUtils(host), [host]);


      const userData: User = {
          _id: userId || "unknown_user",
          name: userName || "Guest",
          avatar: userAvatar || "",
      };

      const botData: User = {
          _id: "onespace_bot",
          name: botName,
          avatar: botAvatar,
      };

      const staffData: User = {
          _id: "onespace_staff",
          name: "Staff",
          avatar: rootUrlAvatar + "/therapist.png",
      };

      useEffect(() => {
          let appState = AppState.currentState;


          const handleAppStateChange = async (nextAppState: AppStateStatus) => {
              if (appState.match(/inactive|background/) && nextAppState === "active") {
                  try {
                      await getNameAndId();
                      const lastVisitTimestampString = await AsyncStorage.getItem(lastChatVisit);
                      const lastVisitTimestamp = lastVisitTimestampString ? JSON.parse(lastVisitTimestampString) : null;
                      const currentTime = Date.now();
                      const inactivityBetweenTwoWelcomes = 6 * 60 * 60 * 1000; // 6 hours in milliseconds


                      // Welcome msg is sent on chat focus it is empty or if the chat the last visit was more than 2 hours ago
                      if (lastVisitTimestamp && currentTime - parseInt(lastVisitTimestamp) > inactivityBetweenTwoWelcomes) {
                          let initSess = await initSession();

                          const _data = await Storage.getStorage();
                          const formStorageData = updatePassportAndWellbeingFormData(_data);
                          const idToken = await refreshIdTokenIfNeeded();

                          if (!idToken) {
                              sendSystemMessage("⚠️ Session expired. Reloading the app...");
                              RNRestart.Restart(); // ⬅️ force restart
                              return;
                          }

                          const slotArray = await RasaUtils.buildRasaContextSlots(_userId, _email, idToken || "");
                          await rasaUtils.updateRasaContext(_userId, slotArray, idToken || "");

                          Analytics.record({
                              name: "NudgeRasaChat",
                              attributes: {
                                  email: _email as string,
                                  university: _university as string,
                              },
                          });
                          await nudge(idToken);
                      }

                  } catch (err) {
                      console.error("Error on app foreground nudge:", err);
                  }
              }
              appState = nextAppState;
          };

          const subscription = AppState.addEventListener("change", handleAppStateChange);

          return () => {
              subscription.remove();
          };
      }, []);

      useEffect(() => {

          if (eventsLoaded) {
              initFirstMessage();
          }

          // Monitor network status
          const unsubscribe = NetInfo.addEventListener((state) => {
              setIsConnected(state.isConnected);
              setErrorMessage(state.isConnected ? null : "No internet connection. Please check your network.");
          });

          return () => unsubscribe();
      }, [eventsLoaded]);


      function sendSystemMessage(endedMessage: string) {
          // Append a message to the chat when the WebSocket disconnects
          const endMessage: IMessage = {
              _id: Math.random().toString(36).substr(2, 9), // Generate a unique ID
              text: endedMessage,
              createdAt: new Date(),
              user: botData, // Ensure it appears as a system message
          };

          setMessages((prevMessages) => GiftedChat.append(prevMessages, [endMessage]));
      }

      function sendSystemReconnect() {
          // Append a message to the chat when the WebSocket disconnects
          const endMessage: IMessage = {
              _id: Math.random().toString(36).substr(2, 9), // Generate a unique ID
              text: "The chat with the mentor has ended, do you want to try reconnect?",
              createdAt: new Date(),
              user: botData,
              quickReplies: {
                  type: "radio", // or "checkbox" if multiple selections are allowed
                  values: [
                      {title: "Reconnect", value: "/query_reconnect"},
                      {title: "End the chat", value: "/query_endchat"}
                  ],
                  keepIt: true // determines if the quick replies should remain visible after selection
              }
          };
          setMessages((prevMessages) => GiftedChat.append(prevMessages, [endMessage]));
      }

      useEffect(() => {
          let isMounted = true;
          const setupWebSocket = async () => {
              if (onetoone) {
                  const idToken = await refreshIdTokenIfNeeded();

                  if (pingInterval.current) {
                      clearInterval(pingInterval.current);
                      pingInterval.current = null;
                  }

                  ws.current = new WebSocket(ONETOONE_HOST + "?token=" + idToken);

                  ws.current.onopen = () => {
                      console.log("Connected to WebSocket server");
                      sendSystemMessage("I'm contacting OneSpace team");
                      if (lastRasaMediumResponse?.text) {
                          ws.current?.send(JSON.stringify({message: lastRasaMediumResponse.text}));
                          setLastRasaMediumResponse(null);
                      }
                      // Start ping loop
                      if (pingInterval.current) clearInterval(pingInterval.current);
                      pingInterval.current = setInterval(() => {
                          if (ws.current?.readyState === WebSocket.OPEN && onetoone) {
                              ws.current.send(JSON.stringify({type: "ping"}));
                          }
                      }, 30000); // every 30 seconds
                  };

                  ws.current.onmessage = (event) => {
                      if (!isMounted) return;
                      try {
                          const data = JSON.parse(event.data);

                          // Handle pong response safely
                          if (
                            (Array.isArray(data) && data.length === 1 && data[0].type === "pong") ||
                            (data && data.type === "pong")
                          ) {
                              return;
                          }

                          const messagesJson: IRasaResponse[] = Array.isArray(data) ? data : [data];
                          const newRecivieMess = parseOneToOneMessages(messagesJson);

                          setIsTyping(true);
                          setMessages((previousMessages) =>
                            GiftedChat.append(previousMessages, newRecivieMess)
                          );

                          setIsTyping(false);
                          setErrorMessage(null);
                      } catch (error) {
                          console.error("Error parsing one to one message:", error);
                      }
                  };

                  ws.current.onerror = (error) => {
                      console.error("WebSocket Error:" + error.message, error);
                      sendSystemMessage("Sorry, a connection error has occurred");
                      // Make sure state is reset if there's an error
                      if (onetoone) {
                          setOnetoone(false);
                      }
                      //setErrorMessage("Connection with the Onespace support has ended.");
                  };

                  ws.current.onclose = () => {
                      console.log("WebSocket connection closed");
                      sendSystemReconnect();
                      setOnetoone(false);
                  };


              }
          };

          setupWebSocket();


          return () => {
              isMounted = false;
              ws.current?.close();
              if (pingInterval.current) {
                  clearInterval(pingInterval.current);
                  pingInterval.current = null;
              }
          };
      }, [onetoone]);


      useFocusEffect(
        useCallback(() => {
            (async () => {

                  try {
                      const idToken = await refreshIdTokenIfNeeded();

                      getNameAndId();

                      if (_userId) {
                          const _data = await Storage.getStorage();
                          const formStorageData = updatePassportAndWellbeingFormData(_data);
                          let initSess = await initSession();
                          Analytics.record({
                              name: "WelcomeRasaChat",
                              attributes: {
                                  email: _email as string,
                                  university: _university as string,
                              },
                          });


                          const slotArray = await RasaUtils.buildRasaContextSlots(_userId, _email, idToken || "");
                          await rasaUtils.updateRasaContext(_userId, slotArray, idToken || "");
                          setEventsLoaded(true);


                      }
                  } catch (error) {
                      console.error("Error updating Rasa context:", error);
                  }
              }
            )();
        }, [_userId])
      );


      // Inner function that cleans bot messages from a psourcarent component
      useImperativeHandle(ref, () => ({
          resetMessages() {
              setMessages([]);
          },
          resetBot() {
              sendMessage("/restart");
          },
          sendCustomMessage(message: string) {
              setMessages((previousMessages) =>
                GiftedChat.append(previousMessages, [
                    createQuickUserReply(message, userData),
                ])
              );
              sendMessage(message);
          },
      }));

      // Check if last message contains a checkbox or not
      const hasLastRasaMessageACheckbox = useMemo(() => {
          if (lastRasaCustomResponse?.custom?.payload?.template_type === "checkbox")
              return true;
          return false;
      }, [lastRasaCustomResponse]);


      // Parse the array message
      const parseRasaMessages = useCallback(
        (messArr: IRasaResponse[]): IMessage[] => {
            return (messArr || [])
              .filter((msg) => !msg.custom)
              .map((singleMess) => createNewBotMessage(singleMess, botData));
        },
        [botData]
      );


      const parseOneToOneMessages = useCallback(
        (messArr: IRasaResponse[]): IMessage[] => {
            return (messArr || []).map((singleMess) =>
              createNewBotMessage(singleMess, staffData)
            );
        },
        [staffData]
      );

      //const rasaUtils = new RasaUtils(host);

      async function getResponsesMedium(responses: IRasaResponse | undefined) {
          let json = JSON.stringify(responses?.custom);
          if (!json) {
              return null;
          }
          const res: IRasaMedium = JSON.parse(json) as IRasaCustomMedium;
          if (res.medium && res.medium.trim() === "onetoone") {
              return res;
          }
          return null;

      }

      async function processRasaMessages(newRecivieMess: IMessage[]) {
          const factorReading = 22;
          let waitingTime = 500;
          let msgLength = 0

          if (newRecivieMess.length == 0) {
              setErrorMessage("🤖 Oops! It seems your assistant is taking a nap... Try waking it up with another message!");
              setIsTyping(false);
          }


          for (let i = 0; i < newRecivieMess.length; i++) {
              const message = newRecivieMess[i];

              setIsTyping(true);
              const responseMessage = {
                  _id: Math.random().toString(36).substr(2, 9), // Unique ID
                  text: "Server response",
                  createdAt: new Date(),
                  user: {
                      _id: 2,
                      name: "Your assistant",
                  },
              };

              await new Promise((resolve) => setTimeout(resolve, waitingTime));
              waitingTime = msgLength * factorReading;
              msgLength = message.text.length
              setMessages((previousMessages) =>
                GiftedChat.append(previousMessages, [message])
              );


              if (i === newRecivieMess.length - 1) {
                  setIsTyping(false);
              }
          }
      }

      function isOneToOne() {
          return onetoone && ws.current && ws.current.readyState === WebSocket.OPEN;
      }

      const sendMessage = useCallback(
        async (text: string): Promise<void> => {
            const session = await getSession();
            if (!session) {
                setErrorMessage("User session expired. Please sign in again.");
                return;
            }

            if (isOneToOne()) {
                ws.current.send(JSON.stringify({message: text}));
            } else {
                await AsyncStorage.setItem(lastChatVisit, JSON.stringify(Date.now()));

                const rasaMessageObj: IRasaMessage = {
                    message: text,
                    sender: `${userId || _userId || "anonymous_user"}`,
                };

                try {
                    if (!isConnected) {
                        setErrorMessage("Cannot send message. No internet connection.");
                        return;
                    }

                    setIsTyping(true);
                    const response = await fetch(`${host}/webhooks/rest/webhook`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            //"authorization": "Bearer " + session.accessToken
                        },
                        body: JSON.stringify(rasaMessageObj),
                    });
                    const messagesJson: IRasaResponse[] = await response.json();

                    let customMessage = messagesJson?.find((message) =>
                      message.hasOwnProperty("custom")
                    );

                    const newRecivieMess = parseRasaMessages(messagesJson);
                    if (!isValidNotEmptyArray(newRecivieMess)) {
                        onEmptyResponse && onEmptyResponse();
                        if (emptyResponseMessage) {
                            const emptyMessageReceive = createBotEmptyMessage(
                              emptyResponseMessage,
                              botData
                            );
                            setMessages((previousMessages) =>
                              GiftedChat.append(previousMessages, [emptyMessageReceive])
                            );
                        }
                        return;
                    }

                    setLastRasaCustomResponse(customMessage);

                    const lastRasaMessageMedium = await getResponsesMedium(customMessage);
                    setLastRasaMediumResponse(lastRasaMessageMedium);

                    await processRasaMessages(newRecivieMess);

                    if (lastRasaMessageMedium) {
                        setOnetoone(true);
                    }

                } catch
                  (error) {
                    console.error("Error sending message:", error);

                    // Add an error message to the chat
                    const errorChatMessage: IMessage = {
                        _id: Math.random().toString(36).substr(2, 9),
                        text: "⚠️ Failed to send message. Please check your network and try again.",
                        createdAt: new Date(),
                        user: botData,
                    };

                    setMessages((prevMessages) => GiftedChat.append(prevMessages, [errorChatMessage]));
                } finally {
                    setIsTyping(false);
                }
            }
        },
        [
            onetoone, ws, userId,
            parseRasaMessages,
            host,
            onSendMessFailed,
            onEmptyResponse,
            emptyResponseMessage,
        ]
      );

      const initFirstMessage = async () => {
          if (!sentFirstMessage.current) {
              sentFirstMessage.current = true;
              let messageText = `/welcome{"first_name": "${_userName}"}`;
              sendMessage(messageText);
          }
      };

      const nudge = async (token) => {
          if (!token) {
              sendSystemMessage("⚠️ Your session has expired or is not active. Please reconnect to continue chatting.");
              return;
          }

          // Ensure latest userId is loaded
          await getNameAndId(); // <-- This will update _userId

          const updatedUserId = _userId || "fallback_user";
          const messageText = `/nudge{"first_name": "${_userName}", "token_id" : "${token}"}`;
          await sendMessage(messageText);
      };

      const initSession = async () => {
          if (!sentFirstMessage.current) {
              let messageText = "/session_start";
              return await sendMessage(messageText);
          }
          return null;
      };

// Hook when send button is pressed
      const onSend = useCallback(
        (messages: IMessage[] = []): void => {

            const currentMessage = messages[0];
            const {text: userText2Rasa = ""} = currentMessage ?? {};
            sendMessage(userText2Rasa);

            setMessages((previousMessages) =>
              GiftedChat.append(previousMessages, messages)
            );
        },
        [sendMessage]
      );

// Hook when Chat Message with Quick Reply options are pressed
      const onQuickReply = useCallback(
        (replies: Reply[]): void => {
            let quickMessage: IMessage[] = [];
            let userText2Rasa: string = "";
            // Case when reply is a radio -> just one option and not a checkbox with 1 option chosen
            if (replies.length <= 1 && !hasLastRasaMessageACheckbox) {
                const {value = "", title = ""} = replies[0] ?? {};
                let quickUserReply = createQuickUserReply(title, userData);
                quickMessage = [quickUserReply];
                userText2Rasa = value;
            }
            // Case when reply is a checkbox -> Multiple options more than 2 options chosen
            else {
                quickMessage = [
                    ...replies.map((reply) =>
                      createQuickUserReply(reply.title, userData)
                    ),
                ];
                const checkboxOptions = replies.map((reply) => reply.value);
                const {payload = "/custom_intent", slot = "custom_slot"} =
                lastRasaCustomResponse?.custom?.payload ?? {};
                const newPayload = JSON.stringify({[slot]: checkboxOptions});
                userText2Rasa = `${payload}${newPayload}`;
            }
            sendMessage(userText2Rasa);
            setMessages((previousMessages) =>
              GiftedChat.append(previousMessages, quickMessage.reverse())
            );


        },
        [userAvatar, sendMessage]
      );


      const CustomizedInputToolbar = (props: InputToolbarProps<IMessage>) => {
          return (
            <InputToolbar
              {...props}
              containerStyle={{
                  backgroundColor: theme.BACKGROUND,
              }}
              renderComposer={(props) => (
                <Composer {...props} textInputStyle={{color: theme.TEXT}}/>
              )}
            />
          );
      };

      const CustomRenderQuickReplies: FC<QuickRepliesProps> = ({
                                                                   onQuickReply,
                                                                   currentMessage,
                                                               }) => {
          const handleReplyPress = (reply: any) => {
              console.log("reply" + JSON.stringify(reply));
              if (reply?.value?.startsWith("https://")) {
                  openExternalURL(reply.value); // open the URL

                  if (onQuickReply) {
                      onQuickReply([{ title: reply.title, value: "/open_link" }]);
                  }

              } else if (onQuickReply) {
                  onQuickReply([reply]); // also send the reply to Rasa
              }
          };

          return (
            <View
              style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 10,
                  justifyContent: "center",
                  alignItems: "center",
                  padding: 10,
              }}
            >
                {currentMessage &&
                  currentMessage.quickReplies?.values.map((reply) => (
                    <TouchableOpacity
                      key={reply.value}
                      onPress={() => handleReplyPress(reply)}
                    >
                        {["Angry", "Sad", "Ok", "Happy", "Great"].includes(reply.title) ? (
                          <RenderRasaButtonIcons
                            title={reply.title as "Angry" | "Sad" | "Ok" | "Happy" | "Great"}
                          />
                        ) : (
                          <Text style={styles.button}>{reply.title}</Text>
                        )}
                    </TouchableOpacity>
                  ))}
            </View>
          );
      };



      const renderChatAvatar = (props: any) => {
          if (!props.currentMessage) return null;

          const dynamicAvatarUrl = `${rootUrlAvatar}/user.png?gender=${encodeURIComponent(_gender)}&university=${encodeURIComponent(_university)}`;

          return (
            <View style={{ padding: 5 }}>
                <Image
                  source={{
                      uri: props.currentMessage.user.avatar || dynamicAvatarUrl,
                  }}
                  style={{ width: 40, height: 40, borderRadius: 20 }}
                />
            </View>
          );
      };

      const renderFooter = () => {
          if (!isTyping && !errorMessage) return null;

          if (isTyping) {
              return (
                <View style={styles.typingIndicator}>
                    <ActivityIndicator size="small" color="#999"/>
                    <Text style={styles.typingText}>Assistant is typing...</Text>
                </View>
              );
          }
          if (errorMessage) {
              return ( // Add `return` here
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{errorMessage}</Text>
                </View>
              );
          }
      };

      async function displayInformation() {
          const storage = await Storage.getStorage();
          sendSystemMessage(JSON.stringify(storage, null, 2));
      }

      return (
        <HeaderWrap
          Icon={
              <TouchableOpacity onPress={() => Keyboard.dismiss()}>
                  <ChatIcon
                    height={40}
                    width={40}
                    color={theme.WHITE}
                    transform={[{translateX: -10}]}
                  />
              </TouchableOpacity>
          }
          showShareIcon={false}
        >
            {_email === "tom+admin@umo.services" && (
              <View style={styles.toggleContainer}>
                  <TouchableOpacity
                    style={styles.toggleButton}
                    onPress={() => setOnetoone((prev) => !prev)}
                  >
                      <Text style={styles.toggleButtonText}>
                          {onetoone ? "Switch to Rasa" : "Switch to WebSocket"}
                      </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.toggleButton}
                    onPress={displayInformation}
                  >
                      <Text style={styles.toggleButtonText}>
                          Info
                      </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.toggleButton}
                    onPress={sendSystemReconnect}
                  >
                      <Text style={styles.toggleButtonText}>
                          Test
                      </Text>
                  </TouchableOpacity>
              </View>
            )}
            <KeyboardAvoidingView
              style={{
                  flex: 1,
                  backgroundColor: theme.BACKGROUND,
                  width: IsMobile ?  Frame.width : WEB_MAXWIDTH, alignSelf: "center",  borderWidth: IsMobile ? 0 : 1, borderStyle: "dashed", borderColor: "lightgrey"
              }}
              behavior={Platform.select({
                  ios: undefined,
                  android: "height",
              })}
              keyboardVerticalOffset={20}
            >
                {webviewURL ? (
                  <WebViewMessage uri={webviewURL}/>
                ) : (

                  <GiftedChat
                    onSend={(mess) => onSend(mess)}
                    messages={messages}
                    onQuickReply={onQuickReply}
                    isLoadingEarlier={true}
                    showUserAvatar={true}
                    renderAvatar={(props) => renderChatAvatar(props)}
                    renderInputToolbar={CustomizedInputToolbar}
                    renderFooter={renderFooter}
                    renderQuickReplies={(props) => {
                        return <CustomRenderQuickReplies {...props} />;
                    }}
//                    renderMessage={CustomRenderMessage}
                    //                    renderChatEmpty={() => { return <Text style={styles.title}>Chat with Personal Assistant</Text>; }}
                    user={userData}
                    {...giftedChatProp}
                  />
                )}
            </KeyboardAvoidingView>
        </HeaderWrap>
      );
  })
;

export default Chat;


const getStyles = (theme: AppTheme) =>
  StyleSheet.create({
      toggleContainer: {
          flexDirection: "row", // Arrange children in a row
          justifyContent: "center", // Center the buttons horizontally
          alignItems: "center",
          marginBottom: 10,
      },
      toggleButton: {
          backgroundColor: "#007AFF",
          paddingVertical: 10,
          paddingHorizontal: 20,
          borderRadius: 10,
          marginHorizontal: 5, // Optional spacing between buttons
      },
      button: {
          backgroundColor: "rgb(35, 110, 150)",
          borderRadius: 20,
          color: "white",
          paddingVertical: 9,
          paddingHorizontal: 20,
          height: 45,
          maxWidth: 400,
          alignItems: "center", // Centers horizontally
          justifyContent: "center",
          overflow: 'hidden'
      },
      title: {
          fontSize: 22,
          fontWeight: "600",
          width: "65%",
          padding: 20
      },
      typingIndicator: {
          flexDirection: "row",
          alignItems: "center",
          padding: 5,
      },
      typingText: {
          marginLeft: 5,
          fontSize: 14,
          color: "#999",
      },
      errorContainer: {
          padding: 10,
          backgroundColor: "#ffe6e6",
          borderTopWidth: 1,
          borderTopColor: "#ffcccc",
      },
      errorText: {
          color: "#cc0000",
          fontSize: 14,
          textAlign: "center",
      },
      webviewContainer: {
          height: 300,
          width: 250,
          borderRadius: 10,
          overflow: "hidden",
          margin: 5,
      },
      loadingContainer: {
          height: 300,
          width: 250,
          justifyContent: "center",
          alignItems: "center",
      }
  });
